import React, { useState, useEffect } from 'react';
import { Bell, User, LogOut } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Modal, Button } from './common';
import './Header.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, userId, userType } = useAuth();
  // 데스크톱 공용 헤더와 모바일 페이지 헤더 모두 같은 진입 조건을 쓴다.
  const canOpenActivityLogs = userType === 'manager' && /^\/academy(?:\/|$)/.test(location.pathname);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [passwords, setPasswords] = useState({
    prevPw: '',
    newPw: '',
    confirmPw: ''
  });
  const [error, setError] = useState('');
  const [isMobileView, setIsMobileView] = useState(() => {
    return typeof window !== 'undefined' && window.innerWidth < 768;
  });

  useEffect(() => {
    const checkMobileView = () => {
      const newIsMobileView = window.innerWidth < 768;
      setIsMobileView(newIsMobileView);
    };

    checkMobileView();
    window.addEventListener('resize', checkMobileView);
    return () => window.removeEventListener('resize', checkMobileView);
  }, []);

  const getUserTypeDisplay = (type) => {
    switch(type) {
      case 'manager':
        return '관리자';
      case 'teacher':
        return '강사';
      default:
        return '';
    }
  };

  const handleLogout = () => {
    setIsModalOpen(false);
    setPasswords({ prevPw: '', newPw: '', confirmPw: '' });
    setError('');
    logout();
    navigate('/signin');
  };

  const handleOpenActivityLogs = () => {
    if (!canOpenActivityLogs) return;
    setIsModalOpen(false);
    setPasswords({ prevPw: '', newPw: '', confirmPw: '' });
    setError('');
    navigate('/academy/activity', {
      state: { activityReturnTo: location.pathname === '/academy/activity' ? '/academy?stats=overview' : `${location.pathname}${location.search}` },
    });
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    const token = localStorage.getItem('accessToken');
    
    if (passwords.newPw !== passwords.confirmPw) {
      setError('새 비밀번호가 일치하지 않습니다.');
      return;
    }

    try {
      const response = await axios.put(`${API_BASE_URL}/api/users/changepw`, 
        {
          prev_pw: passwords.prevPw,
          new_pw: passwords.newPw
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        }
      );

      if (response.data.status === 'success') {
        localStorage.setItem('accessToken', response.data.data.access_token);
        
        setIsModalOpen(false);
        setPasswords({ prevPw: '', newPw: '', confirmPw: '' });
        alert('비밀번호가 성공적으로 변경되었습니다.');
      }
    } catch (error) {
      setError('비밀번호 변경 중 오류가 발생했습니다.');
    }
  };

  return (
    <>
      <header className={`flex h-[50px] items-center bg-headerBg text-white ${!isMobileView ? 'desktop-header' : ''}`} style={{
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.08)',
        position: 'relative',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        width: '100%',
        marginTop: 0
      }}>
        <div className="mx-auto flex w-full items-center justify-between px-4 sm:px-6 lg:px-8">
          <div className="flex items-center">
            <img className="h-8 w-auto" src="/assets/images/hub_logo.png" alt="HUB 로고" />
          </div>
          <div className="flex items-center">
            {/* <button className="p-1 rounded-full text-gray-300 hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-white">
              <Bell className="h-6 w-6" />
            </button> */}
            <div className="relative ml-3 flex items-center">
              {/* 사용자 정보 아이콘 */}
              <Button
                onClick={() => setIsModalOpen(true)}
                variant="primary"
                size="sm"
                className="rounded-lg p-1"
                title="사용자 정보"
              >
                <User size={20} />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setPasswords({ prevPw: '', newPw: '', confirmPw: '' });
          setError('');
        }}
        title="사용자 정보 및 비밀번호 변경"
        size="sm"
        footer={
          <div className="flex w-full items-center justify-between">
            <Button
              variant="secondary"
              onClick={handleLogout}
              className="flex items-center gap-1"
            >
              <LogOut size={12} />
              
            </Button>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                onClick={() => {
                  setIsModalOpen(false);
                  setPasswords({ prevPw: '', newPw: '', confirmPw: '' });
                  setError('');
                }}
              >
                취소
              </Button>
              <Button
                variant="primary"
                onClick={handlePasswordChange}
              >
                변경
              </Button>
            </div>
          </div>
        }
      >
        {/* 사용자 정보 표시 */}
        <div className="mb-6 rounded-lg bg-gray-50 p-4">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">사용자 타입</span>
            {canOpenActivityLogs ? (
              <button
                type="button"
                className="inline-flex cursor-default items-center rounded-full bg-primary px-2.5 py-1 text-xs font-semibold text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                title="더블클릭하여 활동 기록 열기"
                onDoubleClick={handleOpenActivityLogs}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    if (e.repeat) return;
                    handleOpenActivityLogs();
                  }
                }}
              >
                {getUserTypeDisplay(userType)}
              </button>
            ) : (
              <span className="inline-flex items-center rounded-full bg-primary px-2.5 py-1 text-xs font-semibold text-white">
                {getUserTypeDisplay(userType)}
              </span>
            )}
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">아이디</span>
            <span className="text-sm font-medium text-gray-900">{userId || '사용자 없음'}</span>
          </div>
        </div>

        <form>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">기존 비밀번호</label>
              <input
                type="password"
                value={passwords.prevPw}
                onChange={(e) => setPasswords({...passwords, prevPw: e.target.value})}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">새 비밀번호</label>
              <input
                type="password"
                value={passwords.newPw}
                onChange={(e) => setPasswords({...passwords, newPw: e.target.value})}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">새 비밀번호 확인</label>
              <input
                type="password"
                value={passwords.confirmPw}
                onChange={(e) => setPasswords({...passwords, confirmPw: e.target.value})}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
                required
              />
            </div>
            {error && <p className="text-sm text-danger">{error}</p>}
          </div>
        </form>
      </Modal>
    </>
  );
}

export default React.memo(Header);
